import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MessageSquare, MapPin, Paperclip, Send, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import { authService } from '../services/authService';
import { notificationService } from '../services/notificationService';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const ChatRoom = ({ conversation, onBack }) => {
  const { currentUser, userData } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [participantNames, setParticipantNames] = useState({});
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!conversation) return;
    setLoading(true);
    const unsubscribe = chatService.subscribeToMessages(conversation.id, (messagesData) => {
      setMessages(messagesData || []);
      setLoading(false);
    });
    return unsubscribe;
  }, [conversation?.id]);

  useEffect(() => {
    // Fetch participant names
    const fetchParticipantNames = async () => {
      const names = {};
      for (const participantId of conversation.participants) {
        try {
          const userData = await authService.getUserData(participantId);
          names[participantId] = userData?.displayName || userData?.email || 'Unknown User';
        } catch (error) {
          console.error('Error fetching user data:', error);
          names[participantId] = 'Unknown User';
        }
      }
      setParticipantNames(names);
    };

    if (conversation) {
      fetchParticipantNames();
    }
  }, [conversation]);

  // Mark as read when opening or when new messages arrive
  useEffect(() => {
    if (!conversation || !currentUser) return;
    chatService.updateLastRead(conversation.id, currentUser.uid);
  }, [conversation?.id, currentUser?.uid]);

  useEffect(() => {
    if (!conversation || !currentUser) return;
    if (messages.length === 0) return;
    chatService.updateLastRead(conversation.id, currentUser.uid);
  }, [messages.length]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await chatService.sendMessage(conversation.id, {
        text: newMessage.trim(),
        senderId: currentUser.uid,
        senderName: userData?.displayName || currentUser.email,
        type: 'text'
      });
      // Generate notifications for other participants
      try {
        const recipients = conversation.participants.filter((id) => id !== currentUser.uid);
        await Promise.all(
          recipients.map((recipientId) =>
            notificationService.createNotification({
              recipientId,
              type: 'chat_message',
              title: conversation.title || 'New message',
              body: newMessage.trim(),
              resourceType: 'conversation',
              resourceId: conversation.id,
            })
          )
        );
      } catch (e) {
        // Non-blocking
        console.error('Failed to send notification:', e);
      }
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file || !conversation) return;
    setUploading(true);
    const path = `conversations/${conversation.id}/${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type || 'application/octet-stream' });

    uploadTask.on(
      'state_changed',
      () => {},
      (error) => {
        console.error('Upload error:', error);
        setUploading(false);
        event.target.value = '';
      },
      async () => {
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          const isImage = file.type.startsWith('image/');
          const textFallback = isImage ? 'Sent a photo' : `Sent a file: ${file.name}`;

          await chatService.sendMessage(conversation.id, {
            text: textFallback,
            senderId: currentUser.uid,
            senderName: userData?.displayName || currentUser.email,
            type: isImage ? 'image' : 'file',
            fileUrl: url,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size
          });

          try {
            const recipients = conversation.participants.filter((id) => id !== currentUser.uid);
            await Promise.all(
              recipients.map((recipientId) =>
                notificationService.createNotification({
                  recipientId,
                  type: 'chat_message',
                  title: conversation.title || 'New attachment',
                  body: textFallback,
                  resourceType: 'conversation',
                  resourceId: conversation.id,
                })
              )
            );
          } catch (e) {
            console.error('Failed to send attachment notifications:', e);
          }
        } catch (e) {
          console.error('Post-upload handling failed:', e);
        } finally {
          setUploading(false);
          event.target.value = '';
        }
      }
    );
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (message) => {
    return message.senderId === currentUser.uid;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((part) => (part[0] ? part[0].toUpperCase() : ''))
      .join('');
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-sm mx-auto px-6">
          <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-7 h-7 text-slate-400" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-semibold text-slate-800 mb-2">Select a conversation</h3>
          <p className="text-sm text-slate-500">Choose a conversation from the list to view and send messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-50">
      <div className="flex items-center px-4 py-3 border-b border-slate-200 bg-white">
        <button
          onClick={onBack}
          className="mr-3 p-2 hover:bg-slate-100 rounded-md transition-colors text-slate-600 hover:text-slate-800"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <div className="w-10 h-10 bg-slate-200 rounded-lg text-slate-700 flex items-center justify-center font-medium text-sm uppercase flex-shrink-0">
          {conversation.type === 'direct'
            ? getInitials(participantNames[conversation.participants.find(id => id !== currentUser.uid)])
            : conversation.title?.slice(0, 2)?.toUpperCase() || 'GC'}
        </div>
        <div className="flex-1 min-w-0 ml-3">
          <h2 className="text-sm font-semibold text-slate-800 truncate">{conversation.title}</h2>
          {conversation.propertyName && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" strokeWidth={1.75} />
              <p className="text-xs text-slate-500 truncate">{conversation.propertyName}</p>
            </div>
          )}
        </div>
        <span className="text-xs text-slate-500 flex-shrink-0">Active</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-slate-600" />
              <p className="text-sm text-slate-500">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-medium text-slate-800 mb-1">No messages yet</h3>
            <p className="text-xs text-slate-500">Send a message to start the conversation.</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${isOwnMessage(message) ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-md px-4 py-2.5 rounded-lg border text-sm ${
                  isOwnMessage(message)
                    ? 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white border-slate-200 text-slate-800'
                }`}
              >
                {!isOwnMessage(message) && (
                  <div className="text-xs font-medium text-slate-500 mb-1">
                    {participantNames[message.senderId] || message.senderName}
                  </div>
                )}
                <div className="leading-relaxed">
                  {message.type === 'image' && message.fileUrl ? (
                    <a href={message.fileUrl} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={message.fileUrl} alt={message.fileName || 'image'} className="rounded max-h-56" />
                    </a>
                  ) : message.type === 'file' && message.fileUrl ? (
                    <a
                      href={message.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-medium ${
                        isOwnMessage(message) ? 'bg-white/20 hover:bg-white/30' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                      }`}
                    >
                      <FileText className="w-4 h-4" strokeWidth={1.75} />
                      {message.fileName || 'Download file'}
                    </a>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.text}</div>
                  )}
                </div>
                <div className={`text-[11px] mt-1.5 ${isOwnMessage(message) ? 'text-slate-300' : 'text-slate-400'}`}>
                  {formatMessageTime(message.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-slate-200 bg-white">
        <form onSubmit={handleSendMessage} className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="w-full px-4 py-2.5 pr-10 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 placeholder-slate-400"
              disabled={sending}
            />
            <label className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600">
              <input
                type="file"
                className="hidden"
                onChange={handleFileSelected}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              />
              {uploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-slate-500" />
              ) : (
                <Paperclip className="w-4 h-4" strokeWidth={1.75} />
              )}
            </label>
          </div>
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="p-2.5 bg-slate-700 text-white rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <Send className="w-4 h-4" strokeWidth={1.75} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatRoom;
